import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';

/**
 * Market.jsx — Mercado con toggle Internacional / Colombia (Sprint 4, branch feature/dual-balance-v2)
 *
 * Internacional: catálogo de acciones USA + ETFs, datos de Alpaca cuando esté integrado
 * Colombia: Fondos Progresión (Rentar II hero) + Acciones BVC (placeholder)
 *
 * NO se mergea a main hasta que firmen Alpaca + Progresión.
 */
export default function Market() {
  const navigate = useNavigate();
  const [geo, setGeo] = useState('international');
  const [subtab, setSubtab] = useState('populares');
  const [search, setSearch] = useState('');
  const [snapshots, setSnapshots] = useState({});
  const [loading, setLoading] = useState(true);

  // Subtabs disponibles por geografía
  const subtabsByGeo = {
    international: [
      { id: 'populares', label: 'Populares' },
      { id: 'etfs', label: 'ETFs' },
      { id: 'mas_activas', label: 'Más activas' },
      { id: 'por_sector', label: 'Por sector' },
    ],
    colombia: [
      { id: 'fondos', label: 'Fondos' },
      { id: 'acciones_bvc', label: 'Acciones BVC' },
      { id: 'mas_activas_co', label: 'Más activas' },
    ],
  };

  // Cuando cambia el toggle de geografía, reset subtab al primero disponible
  useEffect(() => {
    setSubtab(subtabsByGeo[geo][0].id);
  }, [geo]);

  // Cargar snapshots de Alpaca (precios en vivo) — best-effort
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const symbols = [...POPULARES_US, ...ETFS_US, ...MAS_ACTIVAS_US].map((s) => s.symbol);
        const unique = [...new Set(symbols)].join(',');
        const res = await API.get(`/transaction/getSnapshots?symbols=${unique}`);
        if (!cancelled && res.data?.data) setSnapshots(res.data.data);
      } catch { /* fallback: sin precios live */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredItems = (items) => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((i) =>
      i.symbol.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)
    );
  };

  const renderInternational = () => {
    let items = [];
    if (subtab === 'populares') items = POPULARES_US;
    else if (subtab === 'etfs') items = ETFS_US;
    else if (subtab === 'mas_activas') items = MAS_ACTIVAS_US;
    else if (subtab === 'por_sector') {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SECTORS.map((s) => (
            <div key={s.name} className="bg-white border border-[#E6EBF7] rounded-[14px] p-4 cursor-pointer hover:border-[#0A2540]">
              <p className="text-2xl mb-2">{s.icon}</p>
              <p className="text-sm font-semibold text-[#181D27]">{s.name}</p>
              <p className="text-xs text-[#6B7280] mt-1">{s.examples.join(' · ')}</p>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {filteredItems(items).map((i) => (
          <MarketRow key={i.symbol} {...i} snapshot={snapshots[i.symbol]} onClick={() => navigate(`/dashboard/stock/${i.symbol}`)} />
        ))}
      </div>
    );
  };

  const renderColombia = () => {
    if (subtab === 'fondos') {
      return (
        <div className="space-y-2">
          {FONDOS_PROGRESION.map((f) => (
            <FondoRow key={f.symbol} {...f} />
          ))}
          <p className="text-[11px] text-[#9CA3AF] text-center pt-2">
            Fondos administrados por Progresión SCB · Vigilados por SuperFinanciera
          </p>
        </div>
      );
    }
    if (subtab === 'acciones_bvc') {
      return (
        <div className="bg-white border border-dashed border-[#E6EBF7] rounded-[16px] p-8 text-center">
          <p className="text-4xl mb-3">🚧</p>
          <p className="text-base font-semibold text-[#181D27]">Acciones colombianas próximamente</p>
          <p className="text-sm text-[#6B7280] mt-2 max-w-md mx-auto leading-relaxed">
            Estamos preparando el acceso a las acciones de la Bolsa de Valores de Colombia (BVC).
            Te avisamos en cuanto esté listo.
          </p>
        </div>
      );
    }
    return (
      <div className="text-sm text-[#6B7280] text-center py-8">
        Próximamente · Ranking en tiempo real de los fondos más activos en Colombia
      </div>
    );
  };

  return (
    <div className="px-6 py-6 sm:px-10 sm:py-8 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xl font-semibold text-[#181D27]">Mercado</p>
          <p className="text-sm text-[#535862]">Encuentra dónde invertir tu plata</p>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Buscar acción, ETF o fondo..."
          className="w-full sm:w-64 px-3 py-2 border border-[#E6EBF7] rounded-[10px] text-sm focus:outline-none focus:border-[#0A2540]"
        />
      </div>

      {/* Geo toggle */}
      <div className="flex bg-[#F3F4F6] rounded-[14px] p-1 max-w-md">
        <button
          onClick={() => setGeo('international')}
          className={`flex-1 py-2.5 rounded-[10px] text-sm font-semibold transition ${
            geo === 'international' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#6B7280]'
          }`}
        >
          🌎 Internacional
        </button>
        <button
          onClick={() => setGeo('colombia')}
          className={`flex-1 py-2.5 rounded-[10px] text-sm font-semibold transition ${
            geo === 'colombia' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#6B7280]'
          }`}
        >
          🇨🇴 Colombia
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {subtabsByGeo[geo].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              subtab === t.id ? 'bg-[#0A2540] text-white' : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E6EBF7]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      {loading && geo === 'international' ? (
        <div className="text-center py-8 text-sm text-[#9CA3AF]">Cargando precios...</div>
      ) : geo === 'international' ? renderInternational() : renderColombia()}

      <p className="text-[10px] text-[#9CA3AF] text-center pt-4">
        Sprint 4 · branch feature/dual-balance-v2
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-componentes
// ----------------------------------------------------------------------------

function MarketRow({ symbol, name, sector, snapshot, onClick }) {
  const price = snapshot?.latestTrade?.p || snapshot?.dailyBar?.c;
  const dayPct = snapshot?.dailyBar && snapshot?.prevDailyBar
    ? ((snapshot.dailyBar.c - snapshot.prevDailyBar.c) / snapshot.prevDailyBar.c) * 100
    : null;

  return (
    <div onClick={onClick} className="bg-white border border-[#E6EBF7] rounded-[14px] p-3 flex items-center gap-3 cursor-pointer hover:border-[#0A2540]">
      <div className="w-10 h-10 rounded-[10px] bg-[#F9FAFB] flex items-center justify-center font-bold text-xs text-[#0A2540] flex-shrink-0">
        {symbol}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#181D27] truncate">{name}</p>
        <p className="text-xs text-[#6B7280]">{sector}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-[#181D27]">
          {price ? `$ ${parseFloat(price).toFixed(2)}` : '—'}
        </p>
        {dayPct !== null && (
          <p className={`text-xs font-semibold ${dayPct >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
            {dayPct >= 0 ? '+' : ''}{dayPct.toFixed(2)}%
          </p>
        )}
      </div>
    </div>
  );
}

function FondoRow({ symbol, name, description, ea, ticket_min, hero }) {
  const style = hero
    ? { background: 'linear-gradient(135deg, #FFFBF0, #FEF3C7)', borderColor: '#FCD34D' }
    : { background: 'white', borderColor: '#E6EBF7' };
  return (
    <div className="rounded-[14px] border p-4 cursor-pointer hover:border-[#0A2540]" style={style}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-[12px] bg-white border border-[#FCD34D] flex items-center justify-center font-bold text-xs text-[#0A2540] flex-shrink-0">
            {symbol}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#181D27] truncate">{name}</p>
            <p className="text-xs text-[#6B7280] truncate">{description}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-bold text-[#059669]">{ea}</p>
          <p className="text-[10px] text-[#9CA3AF]">E.A.</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[#FCD34D]/40 flex items-center justify-between text-xs text-[#6B7280]">
        <span>Ticket mínimo: <strong className="text-[#181D27]">${ticket_min}</strong></span>
        <span>Liquidez diaria</span>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Datos estáticos del catálogo (sincronizar con CORE_SYMBOLS de investments-api)
// ----------------------------------------------------------------------------

const POPULARES_US = [
  { symbol: 'AAPL', name: 'Apple', sector: 'Tecnología · USA' },
  { symbol: 'MSFT', name: 'Microsoft', sector: 'Tecnología · USA' },
  { symbol: 'NVDA', name: 'NVIDIA', sector: 'Semiconductores · USA' },
  { symbol: 'TSLA', name: 'Tesla', sector: 'Automotriz · USA' },
  { symbol: 'PLTR', name: 'Palantir', sector: 'Software empresarial · USA' },
  { symbol: 'HIMS', name: 'Hims & Hers Health', sector: 'Telesalud · USA' },
  { symbol: 'DUOL', name: 'Duolingo', sector: 'Educación · USA' },
];

const ETFS_US = [
  { symbol: 'VOO', name: 'S&P 500 ETF', sector: 'Las 500 mayores empresas de USA' },
  { symbol: 'QQQ', name: 'Nasdaq-100 ETF', sector: '100 empresas tech grandes' },
  { symbol: 'SCHD', name: 'Schwab US Dividend', sector: 'Dividendos crecientes' },
  { symbol: 'SOXX', name: 'Semiconductor ETF', sector: 'Sector semiconductores' },
  { symbol: 'ARKK', name: 'ARK Innovation', sector: 'Innovación disruptiva' },
  { symbol: 'VWO', name: 'Emerging Markets', sector: 'Mercados emergentes' },
  { symbol: 'BND', name: 'Total Bond Market', sector: 'Bonos USA · estabilidad' },
  { symbol: 'GLD', name: 'Gold ETF', sector: 'Oro físico · protección' },
];

const MAS_ACTIVAS_US = [
  { symbol: 'NVDA', name: 'NVIDIA', sector: 'Semiconductores · USA' },
  { symbol: 'TSLA', name: 'Tesla', sector: 'Automotriz · USA' },
  { symbol: 'AAPL', name: 'Apple', sector: 'Tecnología · USA' },
  { symbol: 'PLTR', name: 'Palantir', sector: 'Software · USA' },
];

const SECTORS = [
  { name: 'Tecnología', icon: '💻', examples: ['AAPL', 'MSFT', 'NVDA'] },
  { name: 'Salud', icon: '🏥', examples: ['HIMS', 'LLY', 'UNH'] },
  { name: 'Energía', icon: '⚡', examples: ['XOM', 'CVX', 'TSLA'] },
  { name: 'Finanzas', icon: '🏦', examples: ['JPM', 'BAC', 'GS'] },
  { name: 'Consumo', icon: '🛒', examples: ['KO', 'MCD', 'WMT'] },
  { name: 'Inmobiliario', icon: '🏢', examples: ['O', 'VNQ', 'AMT'] },
];

// Fondos Progresión (placeholder hasta partnership formal)
const FONDOS_PROGRESION = [
  { symbol: 'RT-II', name: 'Rentar II', description: 'Inmobiliario · Portafolio de 30 inmuebles en Bogotá y Medellín', ea: '~17%', ticket_min: '100.000', hero: true },
  { symbol: 'CRED', name: 'Fondo Credit', description: 'Renta fija corporativa · Diversificado', ea: '~9%', ticket_min: '500.000', hero: false },
  { symbol: 'LIQ', name: 'Liquidez', description: 'Renta fija corto plazo · Alta liquidez', ea: '~7%', ticket_min: '50.000', hero: false },
];
